import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { mix } from "@spa/utils/assetPathResolver";
import { numberFormat } from '@src/common/service/numberFormatHelper';
import {
    SalaryConstants,
} from '@src/common/service/salary/salaryHelper';
import { useFilter } from '@spa/features/search_job/pc/contexts/FilterContext';
import useSalaryFilterInitializer from '@spa/features/search_job/pc/hooks/salary/useSalaryFilterInitializer';
import useSalaryFilterStateManager from '@spa/features/search_job/pc/hooks/salary/useSalaryFilterStateManager';
import useSalaryFilterActions from '@spa/features/search_job/pc/hooks/salary/useSalaryFilterActions';
import useSalaryDisplayLabel from '@spa/features/search_job/pc/hooks/salary/useSalaryDisplayLabel';

/**
 * Salaryのフィルターコンポーネント
 *
 * - 給与種別ごとにスライダー・入力欄を動的に制御
 * - All選択時やデータ未取得時はスライダー操作不可
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {string} props.salaryCurrency
 * @param {string} props.upTo
 * @param {string} props.from
 * @param {string} props.applyFilter
 * @param {Object} props.salaryTypeOptions
 * @param {Object} props.salaryUnitLabels
 * @param {string} props.salaryTypeParamKey
 * @param {string} props.salaryMinParamKey
 * @param {string} props.salaryMaxParamKey
 * @param {Object} props.translations
 * @param {Object} props.salaryStatistics
 */
const SalaryFilter = ({
    label,
    salaryCurrency,
    upTo,
    from,
    applyFilter,
    salaryTypeOptions,
    salaryUnitLabels,
    salaryTypeParamKey,
    salaryMinParamKey,
    salaryMaxParamKey,
    translations,
    salaryStatistics,
}) => {
    /**
     * 給与フィルターコンポーネント
     * 処理フロー: 初期化 → 状態管理 → フィルター適用 → イベント処理 → 副作用 → 表示用値の準備 → JSX
     */
    // 1. 初期化処理
    const {
        channelName,
        location,
        getInitialSalaryState,
    } = useSalaryFilterInitializer({
        salaryTypeParamKey,
        salaryMinParamKey,
        salaryMaxParamKey,
    });
    const initialSalaryStateRef = useRef(getInitialSalaryState());

    // 2. 状態管理
    const {
        localState,
        isOpen,
        formattedValues,
        isNavigating,
        isSliderDisabledValue,
        rangeMin,
        rangeMax,
        sliderRef,
        sliderInstanceRef,
        minInputRef,
        maxInputRef,
        updateLocalState,
        updateModalState,
        handleSalaryTypeChange,
    } = useSalaryFilterStateManager({
        salaryTypeParamKey,
        salaryMinParamKey,
        salaryMaxParamKey,
        salaryStatistics,
        channelName,
        location,
        initialSalaryState: getInitialSalaryState(),
    });

    // 3. フィルター適用処理
    const { handleApply: handleApplyAction, handleClearClick: handleClearClickAction } = useSalaryFilterActions({
        salaryTypeParamKey,
        salaryMinParamKey,
        salaryMaxParamKey,
        salaryStatistics,
        channelName,
    });

    // 4. イベントハンドラー
    const handleInputChange = useCallback((e, isMin) => {
        const value = e.target.value;
        updateLocalState({ [isMin ? 'salaryMin' : 'salaryMax']: value });
        if (sliderInstanceRef.current && value) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                sliderInstanceRef.current.setHandle(isMin ? 0 : 1, numValue);
            }
        }
    }, [updateLocalState]);

    const handleExternalClear = useCallback((e) => {
        if (e.target.closest(`[data-param-key="${salaryTypeParamKey}"]`)) {
            const clearBtn = e.target.closest('.s-filter_clearBtn');
            if (clearBtn && !e.detail?.reactHandled) {
                e.preventDefault();
                e.stopPropagation();
                handleClearClickAction(e);
            }
        }
    }, [handleClearClickAction, salaryTypeParamKey]);

    const handleOutsideClick = useCallback((e) => {
        if (e.target.closest('.p-filter_select_inner') ||
            e.target.closest(`[data-param-key="${salaryTypeParamKey}"]`)) {
            return;
        }
        updateModalState(false);
    }, [salaryTypeParamKey, updateModalState]);

    // 5. 副作用処理
    useEffect(() => {
        document.addEventListener('click', handleExternalClear, true);
        return () => document.removeEventListener('click', handleExternalClear, true);
    }, [handleExternalClear]);

    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [isOpen, handleOutsideClick]);

    // 6. 表示用値の準備
    const { generateSalaryDisplayLabel } = useSalaryDisplayLabel();
    const displayLabel = generateSalaryDisplayLabel({
        typeId: initialSalaryStateRef.current.salaryType,
        min: initialSalaryStateRef.current.salaryMin,
        max: initialSalaryStateRef.current.salaryMax,
        salaryTypeOptions,
        salaryUnitLabels,
        salaryStatistics,
        translations,
        salaryCurrency,
        upTo,
        from,
        label
    });

    const { getFilterValue } = useFilter();
    const displayIsActive = useMemo(() => {
        if (isNavigating) return false;
        const currentType = getFilterValue(salaryTypeParamKey);
        const currentMin = getFilterValue(salaryMinParamKey);
        const currentMax = getFilterValue(salaryMaxParamKey);
        return (currentType && currentType !== SalaryConstants.ALL_TYPE_ID) ||
            Boolean(currentMin) || Boolean(currentMax);
    }, [isNavigating, getFilterValue, salaryTypeParamKey, salaryMinParamKey, salaryMaxParamKey]);

    const wrapperClassName = [
        "p-filter_btn_wrap", "s-filter_btn_wrap",
        displayIsActive ? "is-active" : "",
        isOpen ? "is-on" : ""
    ].filter(Boolean).join(" ");

    const currentType = getFilterValue(salaryTypeParamKey);
    const currentMin = getFilterValue(salaryMinParamKey);
    const currentMax = getFilterValue(salaryMaxParamKey);
    const showClearButton = !isNavigating && (
        (currentType && currentType !== SalaryConstants.ALL_TYPE_ID) ||
        Boolean(currentMin) || Boolean(currentMax)
    );

    const salaryTypeList = useMemo(() => {
        return Object.entries(salaryTypeOptions).map(([label, value]) => ({
            label, value: value.toString()
        }));
    }, [salaryTypeOptions]);

    // 7. JSX
    return (
        <li>
            <div className={wrapperClassName} data-param-key={salaryTypeParamKey}>
                <button
                    type="button"
                    className="p-filter_btn s-filter_btn"
                    aria-expanded={isOpen}
                    aria-label={translations.item_filter.replace(':item', label)}
                    onClick={() => updateModalState(!isOpen)}
                >
                    {displayLabel}
                </button>
                <button
                    type="button"
                    className={`p-filter_clearBtn${showClearButton ? '' : ' is-hide'} s-filter_clearBtn`}
                    aria-label={translations.clear_filter}
                    onClick={handleClearClickAction}
                >
                    <img
                        src={mix("webfront/images/icon/cross_bold.svg")}
                        className="p-filter_clearBtn_img"
                        alt={``}
                        width="9"
                        height="9"
                    />
                </button>
            </div>
            {isOpen && (
                <div className="p-filter_select_wrap s-filter_menu_box is-show">
                    <div className="p-filter_select_balloon p-filter_select_balloon--w400 s-filter_menu" tabIndex={0} aria-label={`${label} filter`}>
                        <div className="p-filter_select_inner u-pd20 s-filter_menu_inner" style={{ maxHeight: '656px' }}>
                            <ul className="c-radio_flex" role="radiogroup" onClick={e => e.stopPropagation()}>
                                {salaryTypeList
                                    .map(({ label: typeLabel, value }) => (
                                        <li className="c-radio_list" key={value}>
                                            <label className="c-radio_label u-mb15" htmlFor={`s-salary--${typeLabel}`} aria-label={typeLabel}>
                                                <input
                                                    type="radio"
                                                    name="Salary"
                                                    value={value}
                                                    id={`s-salary--${typeLabel}`}
                                                    className={`c-radio_default s-salarySlider_radio${value === SalaryConstants.ALL_TYPE_ID ? ' s-salarySlider_disabled' : ''}`}
                                                    checked={localState.salaryType === value}
                                                    data-salarytype={typeLabel}
                                                    onChange={e => {
                                                        e.stopPropagation();
                                                        handleSalaryTypeChange(value);
                                                    }}
                                                />
                                                <span className="c-radio_btn" aria-hidden="true">{typeLabel}</span>
                                            </label>
                                        </li>
                                    ))}
                            </ul>
                            <div className={`p-salarySlider_wrap s-salarySlider${isSliderDisabledValue ? ' is-disabled' : ''}`}>
                                <div className="p-salarySlider_numBox_wrap">
                                    <div className="p-salarySlider_numBox_outer">
                                        <label className="u-fw_b" htmlFor="input-with-keypress-min">Min({salaryCurrency})</label>
                                        <div className="p-salarySlider_numBox">
                                            <input
                                                ref={minInputRef}
                                                className="p-salarySlider_num c-form_input c-form_input--num s-salarySlider_min"
                                                type="number"
                                                min={rangeMin.toString()}
                                                max={rangeMax.toString()}
                                                step="1"
                                                id="input-with-keypress-min"
                                                name="input-with-keypress-min"
                                                value={
                                                    isSliderDisabledValue
                                                        ? ''
                                                        : localState.salaryMin
                                                }
                                                disabled={isSliderDisabledValue}
                                                onChange={e => handleInputChange(e, true)}
                                            />
                                            <span className="p-salarySlider_numText">{formattedValues.min}</span>
                                        </div>
                                    </div>
                                    <span className="u-mt25">–</span>
                                    <div className="p-salarySlider_numBox_outer">
                                        <label className="u-fw_b" htmlFor="input-with-keypress-max">Max({salaryCurrency})</label>
                                        <div className="p-salarySlider_numBox">
                                            <input
                                                ref={maxInputRef}
                                                className="p-salarySlider_num c-form_input c-form_input--num s-salarySlider_max"
                                                type="number"
                                                min={rangeMin.toString()}
                                                max={rangeMax.toString()}
                                                step="1"
                                                id="input-with-keypress-max"
                                                name="input-with-keypress-max"
                                                value={
                                                    isSliderDisabledValue
                                                        ? ''
                                                        : localState.salaryMax
                                                }
                                                disabled={isSliderDisabledValue}
                                                onChange={e => handleInputChange(e, false)}
                                            />
                                            <span className="p-salarySlider_numText">{formattedValues.max}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-salarySlider_content_wrap">
                                    <div
                                        ref={sliderRef}
                                        className="u-mt40 noUi-target noUi-ltr noUi-horizontal noUi-txt-dir-ltr"
                                        id="slider"
                                    ></div>
                                </div>
                            </div>
                            <div className="p-filter_footer p-filter_footer--borderNone u-mt15">
                                <button className="c-search_btn s-search_btn" type="button" onClick={() => handleApplyAction(localState, sliderInstanceRef)}>{applyFilter}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </li>
    );
};

export default SalaryFilter;